import { useEffect, useRef, useState, type FormEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router";
import { StepUpRequiredDetails, TWO_STEP_SETUP_PATH, normalizeTotpCode, TOTP_CODE_RE, type StepUpRequiredDetails as StepUpDetails } from "@townsy/contracts";
import { isApiError } from "../../api/client";
import { requestStepUp, setStepUpHandler } from "../../api/stepUp";
import { postStepUp, twoFactorApiMessage, twoFactorKeys } from "../../api/twoFactor";
import { Dialog } from "../Dialog";

/**
 * Staff two-step sign-in in the web app (ADR-0039). The server answers every staff request; this only explains its
 * answers:
 *   - MFA_REQUIRED: a panel "Set up two-step sign-in to use staff tools" with a link to the account page section;
 *   - STEP_UP_REQUIRED: the "Enter your code" dialog (StepUpHost, mounted once in App). apiFetch opens it and retries
 *     the refused request once after the server accepted the code. If the dialog was closed, the screen shows a panel
 *     with an "Enter code" button that opens it again.
 */

export type StaffAssuranceIssue = "mfa_required" | "step_up_required";

export function staffAssuranceIssue(error: unknown): StaffAssuranceIssue | null {
  if (!isApiError(error)) return null;
  if (error.code === "MFA_REQUIRED") return "mfa_required";
  if (error.code === "STEP_UP_REQUIRED") return "step_up_required";
  return null;
}

export function MfaRequiredPanel() {
  return (
    <div className="notice notice-warning staff-assurance-panel" role="alert" data-testid="staff-mfa-required">
      <h2>Set up two-step sign-in to use staff tools</h2>
      <p style={{ margin: 0 }}>
        Your account has a Townsy staff role. Staff tools work only after you turn on two-step sign-in with an authenticator app (for example Google
        Authenticator, Microsoft Authenticator or 1Password). It takes about a minute.
      </p>
      <Link className="btn btn-primary btn-sm" to={TWO_STEP_SETUP_PATH} data-testid="staff-mfa-setup-link">
        Set up two-step sign-in
      </Link>
    </div>
  );
}

export function StepUpNeededPanel({ onConfirmed }: { onConfirmed(): void }) {
  const [busy, setBusy] = useState(false);
  const ask = async () => {
    setBusy(true);
    const ok = await requestStepUp(null, { explicit: true });
    setBusy(false);
    if (ok) onConfirmed();
  };
  return (
    <div className="notice notice-info staff-assurance-panel" role="status" data-testid="staff-step-up-needed">
      <h2>Confirm it's you to use staff tools</h2>
      <p style={{ margin: 0 }}>Enter the 6-digit code from your authenticator app. The server records the confirmation for this session.</p>
      <button type="button" className="btn btn-primary btn-sm" onClick={() => void ask()} disabled={busy} data-testid="staff-step-up-open">
        {busy ? "Waiting for your code…" : "Enter code"}
      </button>
    </div>
  );
}

/** The right panel for an MFA_REQUIRED / STEP_UP_REQUIRED answer, or null for any other error. */
export function StaffAssuranceNotice({ error, onRetry }: { error: unknown; onRetry(): void }) {
  const issue = staffAssuranceIssue(error);
  if (issue === "mfa_required") return <MfaRequiredPanel />;
  if (issue === "step_up_required") return <StepUpNeededPanel onConfirmed={onRetry} />;
  return null;
}

/** The dialog's opening sentence for the server's reason. */
export function stepUpIntro(details: StepUpDetails | null): string {
  if (details?.reason === "session_not_verified") return "This session has not confirmed your two-step sign-in yet. Enter the 6-digit code from your authenticator app to use staff tools.";
  if (details?.reason === "step_up_expired") return `Your last confirmation was more than ${details.windowMinutes} minutes ago. Enter a new code from your authenticator app to continue.`;
  if (details?.reason === "step_up_required") return "This is a sensitive staff action. Enter the 6-digit code from your authenticator app to confirm it's you.";
  return "Enter the 6-digit code from your authenticator app to confirm it's you.";
}

/** "Enter your code": posts the code to the server; `onConfirmed` runs only after the server accepted it. */
export function StepUpDialog({
  details,
  onConfirmed,
  onCancel,
  initialError = null,
}: {
  details: StepUpDetails | null;
  onConfirmed(): void;
  onCancel(): void;
  initialError?: string | null;
}) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(initialError);
  const [needsSetup, setNeedsSetup] = useState(false);
  const qc = useQueryClient();
  const clean = normalizeTotpCode(code);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!TOTP_CODE_RE.test(clean)) {
      setError("Enter the 6-digit code from your authenticator app.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await postStepUp(clean);
      void qc.invalidateQueries({ queryKey: twoFactorKeys.all });
      onConfirmed();
    } catch (err) {
      setBusy(false);
      setCode("");
      if (isApiError(err) && err.code === "MFA_REQUIRED") setNeedsSetup(true);
      setError(twoFactorApiMessage(err, "The code could not be checked. Try again."));
    }
  };
  const minutes = details?.windowMinutes ?? 10;
  return (
    <Dialog title="Confirm it's you" onClose={onCancel} closeLabel="Cancel">
      <form className="auth-forms" onSubmit={(e) => void submit(e)} data-movement-block="true" data-testid="step-up-dialog">
        <p style={{ margin: 0 }}>{stepUpIntro(details)}</p>
        <label className="field">
          Code from your authenticator app
          <input
            className="code-input"
            type="text"
            name="one-time-code"
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="[0-9 ]*"
            maxLength={8}
            autoFocus
            required
            value={code}
            onChange={(e) => setCode(e.target.value)}
            aria-describedby="step-up-hint"
            aria-invalid={Boolean(error)}
            data-testid="step-up-code"
          />
          <span id="step-up-hint" className="hint">
            The confirmation lasts {minutes} minutes on this device. Backup codes are only for signing in.
          </span>
        </label>
        {error ? (
          <div className="notice notice-danger small" role="alert" data-testid="step-up-error">
            {error} {needsSetup ? <Link to={TWO_STEP_SETUP_PATH}>Set up two-step sign-in</Link> : null}
          </div>
        ) : null}
        <div className="row">
          <button type="submit" className="btn btn-primary" disabled={busy || needsSetup} data-testid="step-up-confirm">
            {busy ? "Checking…" : "Confirm"}
          </button>
          <button type="button" className="btn" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
        </div>
      </form>
    </Dialog>
  );
}

/**
 * Mounted once (App): registers the step-up handler apiFetch calls on STEP_UP_REQUIRED and shows the dialog while one
 * is pending. Resolves true only after the server accepted a code.
 */
export function StepUpHost() {
  const [pending, setPending] = useState<{ details: StepUpDetails | null } | null>(null);
  const resolver = useRef<((ok: boolean) => void) | null>(null);
  useEffect(
    () =>
      setStepUpHandler(
        (details) =>
          new Promise<boolean>((resolve) => {
            resolver.current = resolve;
            const parsed = StepUpRequiredDetails.safeParse(details);
            setPending({ details: parsed.success ? parsed.data : null });
          }),
      ),
    [],
  );
  const finish = (ok: boolean) => {
    resolver.current?.(ok);
    resolver.current = null;
    setPending(null);
  };
  if (!pending) return null;
  return <StepUpDialog details={pending.details} onConfirmed={() => finish(true)} onCancel={() => finish(false)} />;
}
